import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Download,
  Loader2,
  RefreshCw,
  Search,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  api,
  type ParquetJsonValue,
  type ParquetPageResponse,
} from '@/services/api';

interface ParquetViewerProps {
  path: string;
  filename: string;
  className?: string;
}

const PAGE_SIZE_OPTIONS = [25, 50, 100, 250];

function displayValue(value: ParquetJsonValue | undefined): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function csvValue(value: ParquetJsonValue | undefined): string {
  let text = displayValue(value);
  if (/^[=+\-@]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
}

export function ParquetViewer({ path, filename, className }: ParquetViewerProps) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [filterInput, setFilterInput] = useState('');
  const [filter, setFilter] = useState('');
  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [data, setData] = useState<ParquetPageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestSequence = useRef(0);

  useEffect(() => {
    setPage(1);
    setFilterInput('');
    setFilter('');
    setSortColumn('');
    setSortDirection('asc');
    setData(null);
  }, [path]);

  useEffect(() => {
    const timer = window.setTimeout(() => setFilter(filterInput.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [filterInput]);

  useEffect(() => {
    const requestId = ++requestSequence.current;
    setLoading(true);
    setError(null);

    void api.files.getParquetPage(path, {
      page,
      pageSize,
      filter,
      sortColumn,
      sortDirection,
    }).then((response) => {
      if (requestId !== requestSequence.current) return;
      const lastPage = Math.max(1, Math.ceil(response.filteredRows / pageSize));
      if (page > lastPage) {
        setPage(lastPage);
        return;
      }
      setData(response);
    }).catch((reason: unknown) => {
      if (requestId !== requestSequence.current) return;
      setError(reason instanceof Error ? reason.message : 'Failed to read Parquet file');
    }).finally(() => {
      if (requestId === requestSequence.current) setLoading(false);
    });
  }, [filter, page, pageSize, path, refreshNonce, sortColumn, sortDirection]);

  const pageCount = useMemo(
    () => Math.max(1, Math.ceil((data?.filteredRows ?? 0) / pageSize)),
    [data?.filteredRows, pageSize],
  );

  const handleSort = (column: string) => {
    setPage(1);
    if (sortColumn === column) {
      setSortDirection((current) => current === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortColumn(column);
    setSortDirection('asc');
  };

  const exportVisibleRows = () => {
    if (!data || data.rows.length === 0) return;
    const header = data.columns.map((column) => csvValue(column.name)).join(',');
    const body = data.rows.map((row) => (
      data.columns.map((column) => csvValue(row[column.name])).join(',')
    ));
    const blob = new Blob([`\uFEFF${[header, ...body].join('\r\n')}\r\n`], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${filename.replace(/\.parquet$/i, '')}-page-${page}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  if (error && !data) {
    return (
      <div className={cn('flex h-full items-center justify-center bg-white p-8 dark:bg-surface-950', className)}>
        <div className="max-w-md text-center">
          <AlertCircle className="mx-auto mb-3 h-10 w-10 text-destructive-500" />
          <h2 className="font-semibold text-surface-900 dark:text-surface-100">Could not open this Parquet file</h2>
          <p className="mt-2 text-sm text-surface-500 dark:text-surface-400">{error}</p>
          <Button className="mt-4 gap-2" variant="outline" onClick={() => setRefreshNonce((value) => value + 1)}>
            <RefreshCw className="h-4 w-4" />
            Try again
          </Button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={cn('flex h-full items-center justify-center bg-white dark:bg-surface-950', className)}>
        <div className="text-center">
          <Loader2 className="mx-auto mb-3 h-8 w-8 animate-spin text-primary-500" />
          <p className="text-sm text-surface-500 dark:text-surface-400">Reading {filename}...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex h-full min-h-0 flex-col bg-white dark:bg-surface-950', className)}>
      <div className="flex flex-wrap items-center gap-2 border-b border-surface-200 bg-surface-50 px-3 py-2 dark:border-surface-800 dark:bg-surface-900">
        <div className="relative min-w-52 flex-1 sm:max-w-sm">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-surface-400" />
          <Input
            aria-label="Filter Parquet rows"
            value={filterInput}
            maxLength={256}
            onChange={(event) => {
              setPage(1);
              setFilterInput(event.target.value);
            }}
            placeholder="Filter all columns"
            className="h-8 pl-8"
          />
        </div>
        <span className="text-xs text-surface-500 dark:text-surface-400" aria-live="polite">
          {filter
            ? `${data.filteredRows.toLocaleString()} of ${data.totalRows.toLocaleString()} rows`
            : `${data.totalRows.toLocaleString()} rows`}
          {' · '}{data.columns.length.toLocaleString()} columns
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 gap-1.5"
          onClick={() => setRefreshNonce((value) => value + 1)}
          disabled={loading}
          title="Refresh Parquet data"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Refresh
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 gap-1.5"
          onClick={exportVisibleRows}
          disabled={data.rows.length === 0}
          title="Export the visible page as CSV"
        >
          <Download className="h-4 w-4" />
          Export visible CSV
        </Button>
      </div>

      {error && (
        <div role="alert" className="border-b border-destructive-200 bg-destructive-50 px-3 py-2 text-sm text-destructive-700 dark:border-destructive-900 dark:bg-destructive-950/30 dark:text-destructive-300">
          {error}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-auto">
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0 z-10 bg-surface-100 dark:bg-surface-900">
            <tr>
              {data.columns.map((column) => {
                const activeSort = sortColumn === column.name;
                const SortIcon = !activeSort ? ArrowUpDown : sortDirection === 'asc' ? ArrowUp : ArrowDown;
                return (
                  <th key={column.name} className="border-b border-r border-surface-200 px-3 py-2 text-left align-bottom dark:border-surface-800">
                    <button
                      type="button"
                      onClick={() => handleSort(column.name)}
                      className="group flex w-full items-center gap-2 whitespace-nowrap text-left"
                      aria-label={`Sort by ${column.name}`}
                    >
                      <span>
                        <span className="block font-medium text-surface-800 dark:text-surface-200">{column.name}</span>
                        <span className="block text-[10px] font-normal text-surface-400 dark:text-surface-500">{column.dtype}</span>
                      </span>
                      <SortIcon className={cn('h-3.5 w-3.5', activeSort ? 'text-primary-500' : 'text-surface-300 group-hover:text-surface-500')} />
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className={cn(loading && 'opacity-60')}>
            {data.rows.map((row, rowIndex) => (
              <tr key={data.offset + rowIndex} className="odd:bg-white even:bg-surface-50 hover:bg-primary-50 dark:odd:bg-surface-950 dark:even:bg-surface-900/50 dark:hover:bg-primary-950/20">
                {data.columns.map((column) => {
                  const value = displayValue(row[column.name]);
                  return (
                    <td key={column.name} className="max-w-md border-b border-r border-surface-100 px-3 py-2 text-surface-700 dark:border-surface-900 dark:text-surface-300">
                      {value ? (
                        <span className="block truncate" title={value.length <= 1000 ? value : undefined}>{value}</span>
                      ) : (
                        <span className="text-surface-300 dark:text-surface-700">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {data.rows.length === 0 && (
          <div className="flex h-48 items-center justify-center text-sm text-surface-500 dark:text-surface-400">
            {filter ? 'No rows match this filter.' : 'This Parquet file has no rows.'}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-surface-200 bg-surface-50 px-3 py-2 dark:border-surface-800 dark:bg-surface-900">
        <label className="flex items-center gap-2 text-xs text-surface-500 dark:text-surface-400">
          Rows per page
          <select
            aria-label="Rows per page"
            value={pageSize}
            onChange={(event) => {
              setPage(1);
              setPageSize(Number(event.target.value));
            }}
            className="h-8 rounded-md border border-surface-300 bg-white px-2 text-xs text-surface-700 dark:border-surface-700 dark:bg-surface-950 dark:text-surface-200"
          >
            {PAGE_SIZE_OPTIONS.map((size) => <option key={size} value={size}>{size}</option>)}
          </select>
        </label>
        <div className="flex items-center gap-2">
          <span className="text-xs text-surface-500 dark:text-surface-400">
            Page {page.toLocaleString()} of {pageCount.toLocaleString()}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => setPage((value) => Math.max(1, value - 1))}
            disabled={page <= 1 || loading}
            aria-label="Previous Parquet page"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => setPage((value) => Math.min(pageCount, value + 1))}
            disabled={page >= pageCount || loading}
            aria-label="Next Parquet page"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ParquetViewer;
